import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Landing from "@/pages/Landing";
import MarketingLayout from "@/marketing/MarketingLayout";
import MarketingHome from "@/marketing/pages/Home";
import MarketingFeatures from "@/marketing/pages/Features";
import MarketingHowItWorks from "@/marketing/pages/HowItWorks";
import MarketingIntegrations from "@/marketing/pages/Integrations";
import MarketingProductTour from "@/marketing/pages/ProductTour";
import MarketingResources from "@/marketing/pages/Resources";
import MarketingFAQ from "@/marketing/pages/FAQ";
import MarketingAbout from "@/marketing/pages/About";
import MarketingContact from "@/marketing/pages/Contact";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import VerifyEmail from "@/pages/VerifyEmail";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import AcceptInvite from "@/pages/AcceptInvite";
import Setup from "@/pages/Setup";
import MarketingPricing from "@/marketing/pages/Pricing";
import LaunchStats from "@/marketing/pages/LaunchStats";
import AppHostRedirect from "@/marketing/AppHostRedirect";
import Layout from "@/components/Layout";
import PaywallGuard from "@/components/PaywallGuard";
import Dashboard from "@/pages/Dashboard";
import Settings from "@/pages/Settings";
import Clients from "@/pages/Clients";
import ClientProfile from "@/pages/ClientProfile";
import Intake from "@/pages/Intake";
import IntakeWizard from "@/pages/IntakeWizard";
import ServiceRequests from "@/pages/ServiceRequests";
import ClientOnboarding from "@/pages/ClientOnboarding";
import Contracts from "@/pages/Contracts";
import Invoices from "@/pages/Invoices";
import FormsDocuments from "@/pages/FormsDocuments";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import DataDeletion from "@/pages/DataDeletion";
import Tasks from "@/pages/Tasks";
import Calendar from "@/pages/Calendar";
import Documents from "@/pages/Documents";
import Team from "@/pages/Team";
import Notifications from "@/pages/Notifications";
import Reports from "@/pages/Reports";
import Analytics from "@/pages/Analytics";
import CalendarConnections from "@/pages/CalendarConnections";
import Billing from "@/pages/Billing";
import ClientPortal from "@/pages/ClientPortal";
import Booking from "@/pages/Booking";
import BookingPublic from "@/pages/BookingPublic";
import BookingManage from "@/pages/BookingManage";
import Scheduling from "@/pages/Scheduling";
import Forms from "@/pages/Forms";
import FormResponses from "@/pages/FormResponses";
import PublicForm from "@/pages/PublicForm";
import PortalOnboarding from "@/pages/PortalOnboarding";

function RequireAuth({ children }) {
  const { session, initialized, emailVerified, memberships, membershipsLoaded } = useAuth();
  // Never redirect while auth is still initializing — this is the #1 fix for
  // the "kicked out" bug. The old code let `memberships === []` slip through
  // as "user has no orgs" and bounced to /setup on every refresh.
  if (!initialized) {
    return <div className="min-h-screen flex items-center justify-center text-[#57534E]">Loading…</div>;
  }
  if (!session) return <Navigate to="/login" replace />;
  if (!emailVerified) return <Navigate to="/verify-email" replace />;
  // Only redirect to /setup once we have a confirmed empty memberships list.
  if (!membershipsLoaded) {
    return <div className="min-h-screen flex items-center justify-center text-[#57534E]">Loading your workspace…</div>;
  }
  if (memberships.length === 0) return <Navigate to="/setup" replace />;
  return children;
}

function App() {
  return (
    <div className="App">
      <HelmetProvider>
      <BrowserRouter>
        <AuthProvider>
          <AppHostRedirect>
          <Routes>
            {/* Marketing preview routes (kept alongside Landing until approval to swap /) */}
            <Route path="/launch-stats" element={<LaunchStats />} />
            <Route element={<MarketingLayout />}>
              <Route path="/" element={<MarketingHome />} />
              <Route path="/home" element={<MarketingHome />} />
              <Route path="/features" element={<MarketingFeatures />} />
              <Route path="/how-it-works" element={<MarketingHowItWorks />} />
              <Route path="/integrations" element={<MarketingIntegrations />} />
              <Route path="/product-tour" element={<MarketingProductTour />} />
              <Route path="/resources" element={<MarketingResources />} />
              <Route path="/faq" element={<MarketingFAQ />} />
              <Route path="/about" element={<MarketingAbout />} />
              <Route path="/contact" element={<MarketingContact />} />
              <Route path="/pricing" element={<MarketingPricing />} />
            </Route>
            <Route path="/account-deletion" element={<Navigate to="/data-deletion" replace />} />
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/accept-invite" element={<AcceptInvite />} />
            <Route path="/portal/:token" element={<ClientPortal />} />
            <Route path="/book/:orgSlug" element={<BookingPublic />} />
            <Route path="/book/:orgSlug/staff/:userSlug" element={<BookingPublic />} />
            <Route path="/book/:orgSlug/staff/:userSlug/:aptSlug" element={<BookingPublic />} />
            <Route path="/book/:orgSlug/:aptSlug" element={<BookingPublic />} />
            <Route path="/booking/manage/:token" element={<BookingManage />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/data-deletion" element={<DataDeletion />} />
            <Route path="/setup" element={<Setup />} />
            <Route path="/create-organization" element={<Navigate to="/setup" replace />} />
            <Route
              path="/app"
              element={
                <RequireAuth>
                  <PaywallGuard>
                    <Layout />
                  </PaywallGuard>
                </RequireAuth>
              }
            >
              <Route index element={<Dashboard />} />
              <Route path="clients" element={<Clients />} />
              <Route path="clients/:id" element={<ClientProfile />} />
              <Route path="intake" element={<Intake />} />
              <Route path="intake/new" element={<IntakeWizard />} />
              <Route path="service-requests" element={<ServiceRequests />} />
              <Route path="onboarding" element={<ClientOnboarding />} />
              <Route path="contracts" element={<Contracts />} />
              <Route path="invoices" element={<Invoices />} />
              <Route path="forms-documents" element={<FormsDocuments />} />
              <Route path="tasks" element={<Tasks />} />
              <Route path="calendar" element={<Calendar />} />
              <Route path="documents" element={<Documents />} />
              <Route path="team" element={<Team />} />
              <Route path="notifications" element={<Notifications />} />
              <Route path="reports" element={<Reports />} />
              <Route path="analytics" element={<Analytics />} />
              <Route path="calendar-sync" element={<CalendarConnections />} />
              <Route path="billing" element={<Billing />} />
              <Route path="booking" element={<Navigate to="/app/scheduling" replace />} />
              <Route path="scheduling" element={<Scheduling />} />
              <Route path="forms" element={<Forms />} />
              <Route path="forms/:formId/responses" element={<FormResponses />} />
              <Route path="settings/*" element={<Settings />} />
            </Route>
            <Route path="/f/:slug" element={<PublicForm />} />
            <Route path="/portal/onboarding/:token" element={<PortalOnboarding />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </AppHostRedirect>
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </BrowserRouter>
      </HelmetProvider>
    </div>
  );
}

export default App;
